import { useState } from "react";
import {
  Boxes,
  Droplets,
  Eraser,
  Focus,
  Minus,
  Move,
  MousePointer2,
  Plus,
  RotateCcw,
  Ruler,
  Scissors,
  Settings,
  Trash2,
  Triangle,
  Zap,
} from "lucide-react";
import { MEASURE_UNITS } from "@/viewer-core/measurement";

const VIEWS = [
  ["front", "Front"],
  ["back", "Back"],
  ["left", "Left"],
  ["right", "Right"],
  ["top", "Top"],
  ["iso", "Iso"],
];

function Tool({ icon: Icon, label, active, onClick, testId, disabled = false }) {
  return (
    <button
      type="button"
      data-testid={testId}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`grid h-7 w-7 place-items-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        active
          ? "bg-cyan-600/15 text-cyan-700"
          : "text-slate-500 hover:bg-slate-900/5 hover:text-slate-900"
      }`}
    >
      <Icon size={13} />
    </button>
  );
}

/** Inspector viewer toolbar: navigation modes, preset views, measure, section, reset. */
export function InspectorToolbar({
  mode,
  setMode,
  onFit,
  onReset,
  onView,
  measureTool,
  onMeasureTool,
  measureState,
  onMeasureDelete,
  onMeasureClear,
  measureSettings,
  onMeasureSettings,
  opacity,
  setOpacity,
  explode,
  setExplode,
  sectionMode,
  sectionPos,
  onSectionToggle,
  onSectionMode,
  onSectionPos,
  onSectionClear,
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const measureHint =
    measureTool === "laser"
      ? "Hover a surface · click to keep the cross measurement"
      : measureTool === "angle"
        ? "Click 3 points: arm, vertex, arm"
        : "Click two points";
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-300 bg-white px-2.5 py-1.5">
      <Tool
        icon={MousePointer2}
        label="Select & orbit"
        active={mode === "orbit"}
        onClick={() => setMode("orbit")}
        testId="inspector-tool-orbit"
      />
      <Tool
        icon={Move}
        label="Pan"
        active={mode === "pan"}
        onClick={() => setMode(mode === "pan" ? "orbit" : "pan")}
        testId="inspector-tool-pan"
      />
      <Tool icon={Focus} label="Fit to view" onClick={onFit} testId="inspector-tool-fit" />
      <span className="mx-0.5 h-5 w-px bg-slate-300" />

      {VIEWS.map(([key, label]) => (
        <button
          key={key}
          type="button"
          data-testid={`inspector-view-${key}`}
          onClick={() => onView(key)}
          className="mono h-7 rounded-md px-2 text-[9.5px] uppercase tracking-wider text-slate-500 transition-colors hover:bg-slate-900/5 hover:text-slate-900"
        >
          {label}
        </button>
      ))}

      <span className="mx-0.5 h-5 w-px bg-slate-300" />
      <Tool
        icon={Ruler}
        label="Measure"
        active={mode === "measure"}
        onClick={() => setMode(mode === "measure" ? "orbit" : "measure")}
        testId="inspector-tool-measure"
      />
      {mode === "measure" ? (
        <div
          className="flex items-center gap-1"
          data-testid="inspector-measure-toolbar"
        >
          <Tool
            icon={Ruler}
            label="Distance — pick two points"
            active={measureTool === "distance"}
            onClick={() => onMeasureTool("distance")}
            testId="inspector-measure-distance"
          />
          <Tool
            icon={Zap}
            label="Laser — cross measurement across a surface"
            active={measureTool === "laser"}
            onClick={() => onMeasureTool("laser")}
            testId="inspector-measure-laser"
          />
          <Tool
            icon={Triangle}
            label="Angle — pick arm point, vertex, arm point"
            active={measureTool === "angle"}
            onClick={() => onMeasureTool("angle")}
            testId="inspector-measure-angle"
          />
          <span className="mx-0.5 h-5 w-px bg-slate-300" />
          <Tool
            icon={Trash2}
            label="Delete selected measurement"
            onClick={onMeasureDelete}
            disabled={!measureState?.selectedId}
            testId="inspector-measure-delete"
          />
          <Tool
            icon={Eraser}
            label="Clear all measurements"
            onClick={onMeasureClear}
            disabled={!measureState?.count && !measureState?.pending}
            testId="inspector-measure-clear"
          />
          <span className="relative">
            <Tool
              icon={Settings}
              label="Measurement settings"
              active={settingsOpen}
              onClick={() => setSettingsOpen((open) => !open)}
              testId="inspector-measure-settings"
            />
            {settingsOpen ? (
              <div
                data-testid="measure-settings-panel"
                className="absolute left-0 top-8 z-30 w-52 rounded-lg border border-slate-300 bg-white p-3 shadow-xl"
              >
                <p className="mono mb-1.5 text-[9.5px] uppercase tracking-wider text-slate-500">
                  Unit
                </p>
                <div className="mb-2.5 flex gap-1">
                  {Object.keys(MEASURE_UNITS).map((unit) => (
                    <button
                      key={unit}
                      type="button"
                      data-testid={`measure-unit-${unit}`}
                      onClick={() => onMeasureSettings({ ...measureSettings, unit })}
                      className={`mono h-6 flex-1 rounded text-[10px] uppercase transition-colors ${
                        measureSettings.unit === unit
                          ? "bg-cyan-600/15 text-cyan-700"
                          : "text-slate-500 hover:bg-slate-900/5"
                      }`}
                    >
                      {unit}
                    </button>
                  ))}
                </div>
                <p className="mono mb-1.5 text-[9.5px] uppercase tracking-wider text-slate-500">
                  Decimals
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="measure-decimals-minus"
                    disabled={measureSettings.decimals <= 0}
                    onClick={() =>
                      onMeasureSettings({
                        ...measureSettings,
                        decimals: Math.max(0, measureSettings.decimals - 1),
                      })
                    }
                    className="grid h-6 w-6 place-items-center rounded border border-slate-300 text-slate-600 hover:border-cyan-500/60 disabled:opacity-40"
                  >
                    <Minus size={11} />
                  </button>
                  <span
                    data-testid="measure-decimals-value"
                    className="mono w-5 text-center text-[11px] text-slate-800"
                  >
                    {measureSettings.decimals}
                  </span>
                  <button
                    type="button"
                    data-testid="measure-decimals-plus"
                    disabled={measureSettings.decimals >= 4}
                    onClick={() =>
                      onMeasureSettings({
                        ...measureSettings,
                        decimals: Math.min(4, measureSettings.decimals + 1),
                      })
                    }
                    className="grid h-6 w-6 place-items-center rounded border border-slate-300 text-slate-600 hover:border-cyan-500/60 disabled:opacity-40"
                  >
                    <Plus size={11} />
                  </button>
                </div>
              </div>
            ) : null}
          </span>
        </div>
      ) : null}
      <Tool
        icon={Scissors}
        label="Section planes"
        active={Boolean(sectionMode)}
        onClick={onSectionToggle}
        testId="inspector-tool-section"
      />
      {sectionMode ? (
        <div className="flex items-center gap-1" data-testid="inspector-section-controls">
          {["x", "y", "z"].map((ax) => (
            <button
              key={ax}
              type="button"
              data-testid={`inspector-section-${ax}`}
              onClick={() => onSectionMode(ax)}
              className={`mono h-7 rounded-md px-2 text-[9.5px] uppercase tracking-wider transition-colors ${
                sectionMode === ax
                  ? "bg-cyan-600/15 text-cyan-700"
                  : "text-slate-500 hover:bg-slate-900/5 hover:text-slate-900"
              }`}
            >
              {ax}
            </button>
          ))}
          <button
            type="button"
            data-testid="inspector-section-box"
            onClick={() => onSectionMode("box")}
            className={`mono h-7 rounded-md px-2 text-[9.5px] uppercase tracking-wider transition-colors ${
              sectionMode === "box"
                ? "bg-cyan-600/15 text-cyan-700"
                : "text-slate-500 hover:bg-slate-900/5 hover:text-slate-900"
            }`}
          >
            Box
          </button>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(sectionPos * 100)}
            data-testid="inspector-section-slider"
            onChange={(e) => onSectionPos(Number(e.target.value) / 100)}
            className="h-1.5 w-20 cursor-pointer accent-cyan-600"
          />
          <button
            type="button"
            data-testid="inspector-section-clear"
            onClick={onSectionClear}
            className="mono h-7 rounded-md border border-slate-300 px-2 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
          >
            Clear
          </button>
        </div>
      ) : null}
      <Tool
        icon={RotateCcw}
        label="Reset view"
        onClick={onReset}
        testId="inspector-tool-reset"
      />

      <span className="mx-0.5 h-5 w-px bg-slate-300" />
      <div className="flex items-center gap-1.5" title="Model transparency">
        <Droplets size={12} className="text-slate-500" />
        <input
          type="range"
          min="10"
          max="100"
          value={opacity}
          data-testid="inspector-opacity-slider"
          onChange={(e) => setOpacity(Number(e.target.value))}
          className="h-1.5 w-24 cursor-pointer accent-cyan-600"
        />
        <span
          data-testid="inspector-opacity-value"
          className="mono w-9 text-[10px] text-slate-500"
        >
          {opacity}%
        </span>
      </div>

      <div className="flex items-center gap-1.5" title="Exploded view">
        <Boxes size={12} className="text-slate-500" />
        <input
          type="range"
          min="0"
          max="100"
          value={explode}
          data-testid="inspector-explode-slider"
          onChange={(e) => setExplode(Number(e.target.value))}
          className="h-1.5 w-24 cursor-pointer accent-cyan-600"
        />
        <span
          data-testid="inspector-explode-value"
          className="mono w-9 text-[10px] text-slate-500"
        >
          {explode}%
        </span>
      </div>

      {mode === "measure" && measureState?.count > 0 ? (
        <span
          data-testid="inspector-measure-count"
          className="mono ml-auto flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] text-slate-600"
        >
          {measureState.count} measurement{measureState.count > 1 ? "s" : ""}
        </span>
      ) : null}
      {measureState?.selectedText ? (
        <span
          data-testid="inspector-measure-selected"
          className={`mono flex items-center gap-1.5 rounded-full bg-cyan-50 px-2.5 py-1 text-[10px] text-cyan-700 ${
            mode === "measure" && measureState?.count > 0 ? "" : "ml-auto"
          }`}
        >
          <Ruler size={11} /> {measureState.selectedText}
        </span>
      ) : null}
      {mode === "measure" && !measureState?.count && !measureState?.selectedText ? (
        <span
          data-testid="inspector-measure-hint"
          className="mono ml-auto text-[9.5px] uppercase tracking-wider text-slate-500"
        >
          {measureHint}
        </span>
      ) : null}
    </div>
  );
}
